import type { HistoryFeedFilters, TradeListParams } from '@/types';
import type { RegistryQueryParams, TabBook } from '@/services/trades/trades-service';

export const tradesKeys = {
  all: ['positions'] as const,
  list: (params?: TradeListParams) => [...tradesKeys.all, 'list', params] as const,
  count: (params?: Omit<TradeListParams, 'limit' | 'offset'>) => [...tradesKeys.all, 'count', params] as const,
  allTrades: (params?: Omit<TradeListParams, 'limit' | 'offset'>) => [...tradesKeys.all, 'all', params] as const,
  // Under `all` (['positions']) so the create-trade invalidation refreshes them too.
  infinite: (params?: Omit<TradeListParams, 'limit' | 'offset'>) => [...tradesKeys.all, 'infinite', params] as const,
  attention: (params?: Omit<TradeListParams, 'limit' | 'offset'>) => [...tradesKeys.all, 'attention', params] as const,
  summary: (params?: Omit<TradeListParams, 'limit' | 'offset' | 'ordering'>) => [...tradesKeys.all, 'summary', params] as const,
  detail: (id: number) => [...tradesKeys.all, 'detail', id] as const,
  activity: (id: number) => [...tradesKeys.all, 'activity', id] as const,
  active: () => [...tradesKeys.all, 'active'] as const,
  nearMaturity: () => [...tradesKeys.all, 'near-maturity'] as const,
  marks: (id: number) => [...tradesKeys.all, 'marks', id] as const,
  // LR-565: stock_lb only; bucket is baked into the keys so a repo/all-bucket variant can't
  // collide with these cache slots if one gets added later.
  mtmRunPages: (filters: HistoryFeedFilters) =>
    [...tradesKeys.all, 'mtm-runs', 'stock_lb', 'pages', filters] as const,
  mtmRunBooks: (filters: HistoryFeedFilters) =>
    [...tradesKeys.all, 'mtm-runs', 'stock_lb', 'books', filters] as const,
  mtmRun: (runId: number) => [...tradesKeys.all, 'mtm-runs', 'run', runId] as const,
  mtmRunMarks: (runId: number) => [...tradesKeys.all, 'mtm-run-marks', runId] as const,
  repoMarginCallPreview: (counterpartyName: string, currency: string) =>
    [...tradesKeys.all, 'repo-margin-call', 'preview', counterpartyName, currency] as const,
  // LR-548: authoritative POST /preview/ for a selected subset + New Price overrides. `positionIds`
  // and `overrides` must be pre-sorted by the caller so the key is stable across selection order.
  repoMarginCallPreviewSubset: (
    counterpartyName: string,
    currency: string,
    positionIds: number[] | null,
    overrides: Array<{ position_id: number; new_price: string }> | null,
  ) =>
    [
      ...tradesKeys.all,
      'repo-margin-call',
      'preview-subset',
      counterpartyName,
      currency,
      positionIds,
      overrides,
    ] as const,
  repoMarginCallBalance: (counterpartyName: string) =>
    [...tradesKeys.all, 'repo-margin-call', 'balance', counterpartyName] as const,
  // LR-479: posted-call history for the cash-pool drawer / History subsection. An empty
  // counterpartyName means "all counterparties" (the History subsection's all-CP feed).
  repoMarginCallHistory: (counterpartyName: string, currency: string) =>
    [...tradesKeys.all, 'repo-margin-call', 'history', counterpartyName, currency] as const,
  repoMarginCallPages: (filters: HistoryFeedFilters) =>
    [...tradesKeys.all, 'repo-margin-call', 'pages', filters] as const,
  repoMarginCallBooks: (filters: HistoryFeedFilters) =>
    [...tradesKeys.all, 'repo-margin-call', 'books', filters] as const,
  repoMarginCall: (callId: number) => [...tradesKeys.all, 'repo-margin-call', 'call', callId] as const,
  // LR-565: the frozen per-trade breakdown of one posted call, fetched lazily when its ledger row
  // is expanded (it used to ride along in the history rows and made them megabytes each).
  repoMarginCallLines: (callId: number) =>
    [...tradesKeys.all, 'repo-margin-call', 'lines', callId] as const,
  // LR-661: params OMITTED when absent, so `counterparties()` is a PREFIX of every
  // narrowed entry. `invalidateQueries` matches element-wise, and an `undefined` tail
  // does not match `{is_active:true}` — the trade form's slice escaped invalidation.
  counterparties: (params?: RegistryQueryParams) =>
    params
      ? ([...tradesKeys.all, 'counterparties', params] as const)
      : ([...tradesKeys.all, 'counterparties'] as const),
  // Under `counterparties()`, so a registry write that invalidates it refreshes the tabs too.
  counterpartyTabs: (book: TabBook) => [...tradesKeys.counterparties(), 'tabs', book] as const,
  agreements: (counterpartyId: string) => [...tradesKeys.all, 'agreements', counterpartyId] as const,
  custodians: (params?: RegistryQueryParams) => ['custodians', params] as const,
  holidayCheck: (date: string) => ['holidays', 'check', date] as const,
  holidays: () => ['holidays', 'list'] as const,
  benchmarks: () => ['benchmarks', 'list'] as const,
} as const;

